// Fixed-capacity pool of camera-facing atlas sprites: one instanced draw call. Used twice — particles (sparks,
// pixels, pixel-sort bars, paper, INVOICE glyphs) and label characters (floaters). A sprite's look (colour, cell,
// frame, jitter) is written once when it spawns; its motion (centre, alpha, scale, rotation) every frame it lives.

import {
  BufferAttribute,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  Vector2,
  type Color,
  type ShaderMaterial,
  type Texture,
} from 'three'
import { InvariantViolation } from '@taxwar/net'
import { EMISSIVE_LAYER, assignLayer } from '../render/layers'
import { ATLAS_COLUMNS, ATLAS_ROWS } from './atlas'
import { DirtySpan, additiveMaterial } from './gpu'
import { SPRITE_FRAGMENT, SPRITE_VERTEX } from './shaders'
import type { VfxUniforms } from './uniforms'

const VEC2 = 2
const VEC3 = 3
const VEC4 = 4
const QUAD_CORNERS = [-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]
const QUAD_INDICES = [0, 1, 2, 0, 2, 3]

function instanced(capacity: number, itemSize: number): InstancedBufferAttribute {
  return new InstancedBufferAttribute(new Float32Array(capacity * itemSize), itemSize).setUsage(DynamicDrawUsage)
}

export class SpritePool {
  readonly mesh: Mesh
  private readonly geometry = new InstancedBufferGeometry()
  private readonly material: ShaderMaterial
  private readonly centers: InstancedBufferAttribute
  private readonly motions: InstancedBufferAttribute
  private readonly looks: InstancedBufferAttribute
  private readonly frames: InstancedBufferAttribute
  private readonly jitters: InstancedBufferAttribute
  /** Motion (centre, alpha/scale/rotation) changes every frame a sprite lives; its look only when it spawns. */
  private readonly motionDirty: DirtySpan
  private readonly lookDirty: DirtySpan

  constructor(
    readonly capacity: number,
    atlas: Texture,
    uniforms: VfxUniforms,
  ) {
    this.centers = instanced(capacity, VEC3)
    this.motions = instanced(capacity, VEC3)
    this.looks = instanced(capacity, VEC4)
    this.frames = instanced(capacity, VEC4)
    this.jitters = instanced(capacity, VEC2)
    this.geometry.setAttribute('position', new BufferAttribute(new Float32Array(QUAD_CORNERS), VEC3))
    this.geometry.setIndex(QUAD_INDICES)
    this.geometry.setAttribute('aCenter', this.centers)
    this.geometry.setAttribute('aMotion', this.motions)
    this.geometry.setAttribute('aLook', this.looks)
    this.geometry.setAttribute('aFrame', this.frames)
    this.geometry.setAttribute('aJitter', this.jitters)
    this.geometry.instanceCount = capacity
    this.motionDirty = new DirtySpan([this.centers, this.motions])
    this.lookDirty = new DirtySpan([this.looks, this.frames, this.jitters])
    this.material = additiveMaterial(SPRITE_VERTEX, SPRITE_FRAGMENT, {
      uAtlas: { value: atlas },
      uAtlasGrid: { value: new Vector2(ATLAS_COLUMNS, ATLAS_ROWS) },
      uTime: uniforms.uTime,
      uGlitch: uniforms.uGlitch,
      uGlitchSteps: uniforms.uGlitchSteps,
    })
    this.mesh = new Mesh(this.geometry, this.material)
    // Sprites are placed in the shader and move every frame; a cached bounding sphere would cull them wrongly.
    this.mesh.frustumCulled = false
    assignLayer(this.mesh, EMISSIVE_LAYER)
  }

  /**
   * A sprite's fixed look: tint (may exceed 1 for hot glow), atlas cell, angular width/height, angular screen-space
   * offset from its centre, glitch jitter amplitude and seed.
   */
  setLook(slot: number, colour: Color, intensity: number, cell: number, width: number, height: number, offsetX: number, offsetY: number, jitter: number, seed: number): void {
    this.check(slot)
    this.looks.setXYZW(slot, colour.r * intensity, colour.g * intensity, colour.b * intensity, cell)
    this.frames.setXYZW(slot, width, height, offsetX, offsetY)
    this.jitters.setXY(slot, jitter, seed)
    this.lookDirty.mark(slot, 1)
  }

  /** Per-frame motion; alpha ≤ 0 hides the sprite. */
  setMotion(slot: number, x: number, y: number, z: number, alpha: number, scale: number, rotation: number): void {
    this.centers.setXYZ(slot, x, y, z)
    this.motions.setXYZ(slot, alpha, scale, rotation)
    this.motionDirty.mark(slot, 1)
  }

  hide(slot: number): void {
    this.check(slot)
    this.motions.setX(slot, 0)
    this.motionDirty.mark(slot, 1)
  }

  upload(): void {
    this.motionDirty.flush()
    this.lookDirty.flush()
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.geometry.dispose()
    this.material.dispose()
  }

  private check(slot: number): void {
    if (slot < 0 || slot >= this.capacity) throw new InvariantViolation(`sprite slot ${slot} outside the pool of ${this.capacity}`)
  }
}
